import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'vitaminD', 'unit', 'season',
    'vitaminDDisplay', 'unitDisplay', 'statusContainer', 'statusDot',
    'statusName', 'statusDescription', 'supplementGuidance'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    let vitaminD = parseFloat(this.vitaminDTarget.value) || 0
    const unit = this.unitTarget.value

    if (vitaminD <= 0) {
      return
    }

    // Convert to ng/mL if needed
    if (unit === 'nmoll') {
      vitaminD *= 0.4
    }

    this.vitaminDDisplayTarget.textContent = Math.round(vitaminD)
    this.unitDisplayTarget.textContent = 'ng/mL'

    // Get interpretation
    const result = this.interpret(vitaminD)
    this.updateDisplay(result)
  }

  interpret(value) {
    if (value < 12) {
      return {
        name: 'SEVERELY DEFICIENT',
        color: 'red-600',
        borderColor: 'border-red-600',
        bgColor: 'bg-red-50',
        description: 'Severe vitamin D deficiency. Risk of rickets/osteomalacia, ' +
          'significant bone loss, and immune dysfunction.',
        guidance: 'High-dose supplementation typically needed (50,000 IU weekly or ' +
          '5,000-10,000 IU daily). Consult your healthcare provider for proper ' +
          'protocol and monitoring.'
      }
    }
    if (value < 20) {
      return {
        name: 'DEFICIENT',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Vitamin D deficiency. Associated with bone loss, muscle weakness, ' +
          'and increased infection risk.',
        guidance: 'Supplementation recommended. Typical starting dose: 4,000-5,000 IU ' +
          'daily. Retest in 8-12 weeks. Consider checking calcium and PTH levels.'
      }
    }
    if (value < 30) {
      return {
        name: 'INSUFFICIENT',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Vitamin D insufficiency. Below optimal but not severely deficient.',
        guidance: 'Moderate supplementation helpful. 2,000-4,000 IU daily typically ' +
          'sufficient. Increase sun exposure if possible. Retest in 3 months.'
      }
    }
    if (value < 50) {
      return {
        name: 'SUFFICIENT',
        color: 'green-400',
        borderColor: 'border-green-400',
        bgColor: 'bg-green-50',
        description: 'Sufficient vitamin D for most health purposes. Meets IOM criteria.',
        guidance: 'Maintenance dose of 1,000-2,000 IU daily typically adequate. Higher ' +
          'doses may be beneficial if targeting 40-60 ng/mL. Retest in 6-12 months.'
      }
    }
    if (value <= 80) {
      return {
        name: 'OPTIMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Optimal vitamin D level. In the range many experts recommend ' +
          'for ideal health.',
        guidance: 'Maintain current supplementation (typically 1,000-2,000 IU daily). ' +
          'Annual testing is usually sufficient. No changes needed.'
      }
    }
    if (value <= 100) {
      return {
        name: 'HIGH',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Vitamin D on the higher end. Still generally safe but above ' +
          'most optimal targets.',
        guidance: 'Consider reducing supplementation dose. No immediate concern but ' +
          'no additional benefit at this level. Retest in 3 months after dose adjustment.'
      }
    }
    return {
      name: 'POTENTIALLY TOXIC',
      color: 'red-600',
      borderColor: 'border-red-600',
      bgColor: 'bg-red-50',
      description: 'Vitamin D toxicity risk. Can cause hypercalcemia (high blood ' +
        'calcium) with symptoms like nausea, weakness, kidney problems.',
      guidance: 'STOP all vitamin D supplements immediately. Consult your healthcare ' +
        'provider. Check calcium levels. Toxicity is treatable when caught early.'
    }
  }

  updateDisplay(result) {
    this.statusDotTarget.className = `w-4 h-4 bg-${result.color}`
    this.statusNameTarget.textContent = result.name
    this.statusDescriptionTarget.textContent = result.description
    this.supplementGuidanceTarget.textContent = result.guidance
    this.statusContainerTarget.className = `p-6 ${result.borderColor} border-2 ${result.bgColor}`
  }
}
